import { AppAnalytics } from "@/analytics";
import { debounce, isNotEmpty, once } from "@/fp";
import { RegionUrlStorage, UserStorage } from "@/storage";
import axios from "axios";
import {
  FeatureId,
  LaunchEdition,
  LoggedUser,
  POPULATION_TYPE,
  PopulationTypeHelper,
} from "../models";
import LoginModal from "@/components/modals/LoginModal.vue";
import { state } from "./state";
import { sendAuditLogConsentCode } from "../api/integrations.api";
import { SharedState } from "./index";
import router from "@/router";
import { ssoLogin, ssoSingleUserLogin } from "@/views/login/api";
import { ToastType, UseToast } from "@/components/root/toast/toast.state";
import { env } from "@/env";
import * as AdminRestrictions from "../models/admin-restrictions.model";
import RestrictedModal from "@/components/modals/RestrictedModal.vue";
import { computed, ref } from "@vue/composition-api";
import { AxisType } from "@/gql/types";
import { UseModal } from "@/components/root/modal";
const { action, getter, m, readOnlyAppState } = state;

// === INIT ====
// #9957
export const login = action<{ [key: string]: string }>(
  "login",
  async ({ mutate, dispatch, state }, params) => {
    if (params.code && params.state === "login") {
      const method =
        state.env.type === "Regular" ? ssoLogin : ssoSingleUserLogin;
      const res = await method({ code: params.code });
      if (res.success) {
        const { regionUrl, token } = res.value;
        const is_US_user = regionUrl && regionUrl.search("us-") > -1;
        const base_url = is_US_user
          ? "https://us-intelligence.cognni.ai"
          : location.origin;
        window.open(base_url + "?token=" + encodeURIComponent(token), "_self");
      } else {
        UseToast().show({
          type: ToastType.ALERT,
          msg: "Login Failed",
          duration: 5000,
        });
      }
      return;
    }
    if (params.token) {
      dispatch(onTokenFromUrl, params.token);
      return;
    }

    const user = UserStorage.get();
    if (user) {
      // set user from storage
      mutate(setLoggedUser, user);
      axios.defaults.headers.authorization = user.authorization;
    } else {
      logout();
    }
  }
);

export const auditLogConcent = action<string>(
  "audit log concent",
  async ({ mutate }, code) => {
    const res = await sendAuditLogConsentCode(code);
    if (res.success) {
      mutate(setLoggedUser, {
        ...loggedUser.value,
        auditLogConsent: true,
        //  isProcessingOrg: true,
      });
      AppAnalytics.report({
        component: "Onboarding",
        eventName: "Onboarding_auditlog_consent[Status:succeed]",
      });
    } else {
      AppAnalytics.report({
        component: "Onboarding",
        eventName: "Onboarding_auditlog_consent[Status:failed]",
      });
    }
  }
);

//#region Restrictions
const user_restrictions = getter((s) => {
  // @ts-ignore
  const restrictions = s.user.restrictions || [];
  return AdminRestrictions.to_restriction_object(restrictions);
});

// @ts-ignore
export const is_sys_user = getter((s) => s.user.type === "system");
// @ts-ignore
export const is_biz_user = getter((s) => s.user.type === "business");

const show_restrictions_modal = async (
  type: "info" | "entity" | "app" | "role",
  name: string
) => {
  const res = await UseModal().show(RestrictedModal, { type, name });
  if (res.type === "success") {
    router().push({ name: "Admins" });
  }
};

export const restrictions = {
  user_restrictions,
  is_restricted_info: (info_type_code: number): boolean => {
    return user_restrictions.value.info.includes(info_type_code);
  },
  is_restricted_entity: (entity_type: POPULATION_TYPE): boolean => {
    return user_restrictions.value.entities.includes(entity_type);
  },
  is_restricted_axis_type: (type: AxisType): boolean => {
    const entity_type = PopulationTypeHelper.fromAxisType(type);
    return user_restrictions.value.entities.includes(entity_type);
  },
  is_restricted_app: (app_id: AdminRestrictions.RestrictedAppName): boolean => {
    return user_restrictions.value.apps.includes(app_id);
  },
  show_restrictions_modal,
};
//#endregion

export const loggedUser = getter((s) => s.user);

export const is_demo_env = computed(
  () => loggedUser.value.orgName == "Astro Ventures"
);

export const isUserLogged = getter(
  (s) => isNotEmpty(s.user) && isNotEmpty(s.user.authorization)
);

export const isShowAuditlogModal = getter((s) => {
  const { auditLogConsent, services = [] } = s.user;
  return !auditLogConsent && services.includes("Microsoft");
});

export const setLoggedUser = m.set("user");

export const onTokenFromUrl = action<string>(
  "on token from url",
  async ({ mutate }, token) => {
    const user = {
      services: [],
      dataSourceApplications: [],
      trial: {
        active: false,
        endsAt: "",
        exhausted: false,
      },
      showUI: false,
      partnerId: "",
      paymentEdition: LaunchEdition,
      authorization: "Bearer " + decodeURIComponent(token),
      orgContactEmail: "",
      email: "?",
      name: "?",
      orgName: "?",
      auditLogConsent: false,
    } as LoggedUser;
    mutate(setLoggedUser, user);
    UserStorage.save(user);
    axios.defaults.headers.authorization = user.authorization;

    SharedState.onReady(() => {
      console.log("USER_TO_SEND", SharedState.loggedUser.value);
      const { name, email, orgName } = SharedState.loggedUser.value;
      setTimeout(() => {
        AppAnalytics.report({
          component: "General",
          eventName: "Login_success",
          payload: {
            name,
            email,
            orgName,
          },
        });
      }, 200);
    });
  }
);

export const rejected_user_email = ref("");

export const logout = async (payload?: {
  email?: string;
  explicit?: boolean;
}) => {
  SharedState.resetPaymentReminderModal();
  UserStorage.remove();
  RegionUrlStorage.remove();
  axios.defaults.baseURL = (await env()).server;
  setLoggedUser({
    ...loggedUser.value,
    authorization: "",
  });
  if (readOnlyAppState.value.shared.env.env === "demo") {
    const { UseModal } = await import("@/components/root/modal/modal.state");
    const res = await UseModal().show(LoginModal);
    if (res.type === "success") {
      const _env = await env();
      SharedState.init(_env);
    }
  } else {
    if (payload && payload.email) {
      SharedState.rejected_user_email.value = payload.email;
    }
    router().replace({ name: "Login" });
    setTimeout(() => {
      SharedState.forceInit();
    }, 200);
  }

  if (payload && payload.explicit) {
    logout_from_ms();
  }
  /*   if (payload && payload.email) {
    logout_from_ms();
  } */
};

export const logout_from_ms = () => {
  window.open(
    `https://login.microsoftonline.com/common/oauth2/v2.0/logout?
post_logout_redirect_uri=${encodeURIComponent(location.origin)}`,
    "_self"
  );
};
